
import { Component, Vue } from 'vue-property-decorator';

import { sharedState } from '../../../../../framework/state';
import { deleteResource, getResourcePagingNew, getResourcesNew } from '../../../../../framework/client/resource';
import { getDatabaseOptions, getEnumerationOptions, getOptionLabel } from '../../../../../framework/service/options';

import AutoCompleteMultipleField from '../../../../fields/AutoCompleteMultipleField.vue';
import SelectField from '../../../../../framework/fields/SelectField.vue';
import SelectComponent from '../../../../../framework/fields/SelectComponent.vue';

import { PurchaseInvoiceRowMapping } from '../../../../../../common/application/model/PurchaseInvoiceRowMapping';
import { VendorContractType } from '../../../../../../common/application/model/enums/VendorContractType';
import { ApplicationResource } from '../../../../../../common/application/enumeration/ApplicationResource';
import { VendorType } from '../../../../../../common/application/model/enums/VendorType';
import { InvoicePeriodizationType } from '../../../../../../common/application/model/enums/InvoicePeriodizationType';

import { getVendorOptions } from '../../../../service/application_options_service';
import { errorToast, successToast } from '../../../../service/toast_service';
import ParameterService from '../../../../service/parameter_service';

@Component({
    components: { SelectComponent, SelectField, AutoCompleteMultipleField },
})
export default class PurchaseInvoiceRowMappings extends Vue {
    readonly resourceType = ApplicationResource.PURCHASE_INVOICE_ROW_MAPPING;
    parameterService = new ParameterService(this.resourceType);

    shared = sharedState;
    local = {
        rows: [] as PurchaseInvoiceRowMapping[],
        total: 0,
        loading: false,
        selected: undefined as PurchaseInvoiceRowMapping | undefined,
        vendorTypeOptions: [] as { id: string | undefined; label: string }[],
        vendorOptions: [] as { id: string | undefined; label: string }[],
        invoiceRowTypeOptions: [] as { id: string | undefined; label: string }[],
        vendorContractTypeOptions: [] as { id: string | undefined; label: string }[],
        invoicePeriodizationTypeOptions: [] as { id: string | undefined; label: string }[],
        parameters: this.parameterService.get(),
    };

    async mounted() {
        this.local.vendorTypeOptions = getEnumerationOptions('VendorType', Object.keys(VendorType), this);
        this.local.vendorOptions = await getVendorOptions();
        this.local.invoiceRowTypeOptions = await getDatabaseOptions('InvoiceRowType', this);
        this.local.vendorContractTypeOptions = getEnumerationOptions(
            'VendorContractType',
            Object.keys(VendorContractType),
            this,
        );
        this.local.invoicePeriodizationTypeOptions = getEnumerationOptions(
            'InvoicePeriodizationType',
            Object.keys(InvoicePeriodizationType),
            this,
        );

        await this.loadAsyncData();
    }

    async loadAsyncData() {
        this.local.loading = true;

        const backendParameters = this.parameterService.getBackendParameters();

        this.local.total = (await getResourcePagingNew(this.resourceType, backendParameters)).rowCount;
        this.local.rows = [];
        const rows = (await getResourcesNew(
            this.resourceType,
            this.local.parameters.page,
            backendParameters,
        )) as Array<PurchaseInvoiceRowMapping>;

        rows.forEach((row) => {
            this.local.rows.push(row);
        });
        this.local.loading = false;
        if (this.local && this.local.rows.indexOf(this.local.selected!!) == -1) {
            this.local.selected = undefined;
        }
    }

    async onPageChange(page: number) {
        this.local.parameters.page = page;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }

    onRowClick(row: PurchaseInvoiceRowMapping): void {
        if (this.local.selected && row.id === this.local.selected.id) {
            this.local.selected = undefined;
        }
    }

    add() {
        this.$router.push(`/${this.resourceType}/add`);
    }

    edit(id?: string) {
        if (id) {
            this.$router.push(`/${this.resourceType}/${id}/edit`);
        } else if (this.local.selected) {
            this.$router.push(`/${this.resourceType}/${this.local.selected.id}/edit`);
        }
    }

    confirmDelete() {
        this.$buefy.dialog.confirm({
            title: this.$t('title.confirmDelete').toString(),
            message: this.$t('message.confirmDelete').toString(),
            cancelText: this.$t('button.cancel').toString(),
            confirmText: this.$t('button.ok').toString(),
            type: 'is-success',
            onConfirm: async () => {
                if (this.local.selected) {
                    try {
                        await deleteResource(this.resourceType, this.local.selected.id);
                        successToast(this, 'message.resourceDeleted');
                        await this.loadAsyncData();
                    } catch (e) {
                        errorToast(this);
                    }
                }
            },
        });
    }

    dropdownClosed(resourceIds: string[], idField: string): void {
        if (this.local.loading) {
            return;
        }

        if (!resourceIds.length) {
            delete this.local.parameters[idField];
        } else {
            this.local.parameters[idField] = resourceIds;
        }

        this.resetPagingAndLoadData();
    }

    async resetFilter(): Promise<void> {
        this.local.parameters = this.parameterService.getDefault();
        await this.resetPagingAndLoadData();
    }

    getVendorTypeLabel(id: string): string {
        return getOptionLabel(id, this.local.vendorTypeOptions);
    }

    getVendorName(id: string): string {
        return getOptionLabel(id, this.local.vendorOptions);
    }

    getInvoiceRowType(id: string): string {
        return getOptionLabel(id, this.local.invoiceRowTypeOptions);
    }

    getVendorContractTypeType(id: string): string {
        return getOptionLabel(id, this.local.vendorContractTypeOptions);
    }

    getInvoicePeriodizationType(id: string): string {
        return getOptionLabel(id, this.local.invoicePeriodizationTypeOptions);
    }

    shortenVendorProductPattern(vendorProductPattern: string) {
        if (vendorProductPattern) {
            if (vendorProductPattern.length > 30) {
                return vendorProductPattern.substring(0, 30) + '...';
            } else {
                return vendorProductPattern;
            }
        } else {
            return vendorProductPattern;
        }
    }

    get isAdmin(): boolean {
        return this.shared.admin;
    }

    get vendorIds(): string[] {
        return this.local.parameters.vendorId || [];
    }

    private async resetPagingAndLoadData() {
        this.local.parameters.page = 1;
        this.parameterService.set(this.local.parameters);
        await this.loadAsyncData();
    }
}
